import { DEFAULT_SYMBOL_SIZE } from '@atlassian/jira-servicedesk-insight-graphs-force-graph/src/common/constants.tsx';
import type {
	EchartsGraphNode,
	EchartsGraphEdge,
	EchartsGraphData,
} from '@atlassian/jira-servicedesk-insight-graphs-force-graph/src/common/types.tsx';
import { DEFAULT_LINE_STYLE } from '@atlassian/jira-servicedesk-insight-graphs-force-graph/src/common/utils/graph-config/index.tsx';
import type {
	InsightObjectGraph,
	Node,
	Edge,
} from '@atlassian/jira-servicedesk-insight-shared-types/src/common/types/cmdb-object-graph.tsx';
import type { ReferenceType } from '../controllers/configuration/types.tsx';

const normalizeNode = (n: Node): EchartsGraphNode => ({
	id: `${n.type}${n.id}`,
	value: n.id,
	/**
	 * TODO We should consider that 'Object Type' and 'Object' nodes are
	 * visually identical but behave differently when clicked. The server
	 * version of the graph gets around this by appending (Type) to 'Object
	 * Type' node names. However that approach would require injecting intl
	 * functions into this transformer function which isn't ideal in this case.
	 */
	name: n.name,
	draggable: true,
	symbol: `image://${n.icon}`,
	symbolSize: DEFAULT_SYMBOL_SIZE,
	context: {
		type: n.type,
		name: n.name,
		id: n.id,
		icon: n.icon,
		objectSchemaId: n.objectSchemaId,
	},
});

const normalizeEdge = (e: Edge): EchartsGraphEdge => ({
	name: e.label,
	source: e.from,
	target: e.to,
	lineStyle: {
		color: e.color,
		...DEFAULT_LINE_STYLE,
	},
	symbolSize: 8,
});

export const getNormalizedGraphData = (insightGraphData?: InsightObjectGraph): EchartsGraphData => {
	const { nodes = [], edges = [] } = insightGraphData ?? {};

	const normalizedEdges = edges.map(normalizeEdge);
	const normalizedNodes = nodes.map(normalizeNode);

	return {
		nodes: normalizedNodes,
		edges: normalizedEdges,
	};
};

// filtered based on selected reference types in the graph configuration panel
export const getFilteredEdgesAndNodes = (
	objectGraphData?: InsightObjectGraph,
	// @ts-expect-error - TS1016 - A required parameter cannot follow an optional parameter.
	referenceTypes: ReferenceType[],
	selectedObjectId: string,
): InsightObjectGraph => {
	const { nodes = [], edges = [] } = objectGraphData ?? {};
	const enabledReferenceTypes = referenceTypes.filter((r) => r.checked === true);

	const updatedEdges = edges.filter(
		(edge) =>
			enabledReferenceTypes.some((r) => r.to.includes(edge.to)) &&
			enabledReferenceTypes.some((r) => r.from.includes(edge.from)),
	);
	const updatedNodes = nodes.filter(
		(node) =>
			// always include the currently selected node (even with no outgoing edges)
			node.id === selectedObjectId ||
			updatedEdges.find((e) => e.to.slice(1) === node.id) ||
			updatedEdges.find((e) => e.from.slice(1) === node.id),
	);

	return { nodes: updatedNodes, edges: updatedEdges };
};

// only render nodes / edges that have a connection to the currently selected parent node
export const getConnectedNodesFromParent = (
	objectGraphData?: InsightObjectGraph,
	// @ts-expect-error - TS1016 - A required parameter cannot follow an optional parameter.
	parentObjectId: string,
): InsightObjectGraph => {
	const { nodes = [], edges = [] } = objectGraphData ?? {};

	const parentObject = nodes.find((n) => n.id === parentObjectId);
	const updatedNodes = parentObject ? [parentObject] : [];
	const updatedEdges: Array<Edge> = [];

	// TODO: can potentially refactor the way this logic works to be more efficient
	// every time we add a new connection, we want to re-iterate to ensure we add additional valid connections to those new nodes we added in the previous iteration
	let shouldTraverseEdges = true;
	while (shouldTraverseEdges) {
		shouldTraverseEdges = false;
		for (const edge of edges) {
			const isAlreadyAdded = updatedEdges.some((existingEdge) => existingEdge === edge);
			const hasValidConnection = updatedNodes
				.filter(Boolean)
				.some((n) => n.id === edge.to.slice(1) || n.id === edge.from.slice(1));

			if (!isAlreadyAdded && hasValidConnection) {
				shouldTraverseEdges = true;
				updatedEdges.push(edge);
				// whenever we add a valid edge we go through the existing nodes and add those that are connected via the edge to the result
				nodes.forEach((node) => {
					if (
						!updatedNodes.includes(node) &&
						(node.id === edge.to.slice(1) || node.id === edge.from.slice(1))
					) {
						updatedNodes.push(node);
					}
				});
			}
		}
	}

	return { nodes: updatedNodes, edges: updatedEdges };
};
